import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { motion, AnimatePresence } from 'motion/react'
import { X, Check } from 'lucide-react'

interface ImageCropModalProps {
  file: File
  aspect?: number
  onCancel: () => void
  onConfirm: (cropped: File) => void
}

async function decodeRespectingExif(file: File): Promise<ImageBitmap> {
  // Modern browsers auto-rotate `<img>` rendering based on EXIF, so
  // react-easy-crop's preview — and the pixelCrop coords it returns — are in
  // the post-rotation space. Canvas drawImage with a plain Image() does NOT
  // rotate, so we need an EXIF-aware decoder. createImageBitmap with
  // imageOrientation:'from-image' gives a bitmap whose dimensions match what
  // the preview saw; the crop coords line up exactly.
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    // Older Safari rejects the option object. Fall back to raw decode — the
    // crop will be correct for any image without an EXIF rotation, which is
    // every desktop upload.
    return await createImageBitmap(file)
  }
}

async function cropToFile(
  file: File,
  pixelCrop: Area,
  filename: string,
): Promise<File> {
  const bitmap = await decodeRespectingExif(file)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(
      bitmap,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    )

    // JPEGs are smaller than PNGs for photos and match the backend's `.jpg`
    // suffix in upload_collection_item_image_to_s3. Quality 0.92 mirrors what
    // most browser canvas-export defaults converge on.
    const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas toBlob returned null'))),
        outType,
        0.92,
      ),
    )
    return new File([blob], filename, { type: outType })
  } finally {
    bitmap.close()
  }
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  file,
  aspect = 1,
  onCancel,
  onConfirm,
}) => {
  const imageSrc = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(imageSrc), [imageSrc])

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [pixelCrop, setPixelCrop] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixelCrop(areaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!pixelCrop) return
    setBusy(true)
    try {
      const baseName = file.name.replace(/\.[^.]+$/, '')
      const ext = file.type === 'image/png' ? 'png' : 'jpg'
      const cropped = await cropToFile(
        file,
        pixelCrop,
        `${baseName}-cropped.${ext}`,
      )
      onConfirm(cropped)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        <motion.div
          className="bg-white rounded-lg w-full max-w-2xl flex flex-col shadow-xl"
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">Crop image</h2>
            <motion.button
              type="button"
              onClick={onCancel}
              className="p-1 hover:bg-gray-100 rounded-lg"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              aria-label="Cancel crop"
            >
              <X className="w-5 h-5" />
            </motion.button>
          </div>

          <div className="relative w-full h-80 sm:h-96 bg-gray-900">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid
            />
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-600 w-12 shrink-0">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-amber-600"
                aria-label="Zoom"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy || !pixelCrop}
                className="px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {busy ? 'Cropping…' : 'Use crop'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ImageCropModal
